import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useChangePassword } from '../api/queries';
import { ROLE_LABELS, type User } from '../api/types';
import { PlayerVerificationSection } from '../components/PlayerVerificationSection';
import { TokenSection } from '../components/TokenSection';
import { useToast } from '../components/Toast';

export function AccountPage({ user }: { user: User }) {
  const changePassword = useChangePassword();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const error = changePassword.error instanceof ApiError ? changePassword.error : null;
  const fieldError = (field: string): string | undefined =>
    error?.fields[field] ?? (error?.field === field ? error.message : undefined);
  const generalError = error && !error.field ? error.message : undefined;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    changePassword.mutate(
      { currentPassword, newPassword, newPasswordConfirm },
      {
        onSuccess: () => {
          toast.push('success', 'Passwort wurde geändert.');
          setCurrentPassword('');
          setNewPassword('');
          setNewPasswordConfirm('');
        },
      },
    );
  };

  return (
    <div className="stack-narrow">
    <section className="card">
      <h1>Mein Konto</h1>

      <dl className="meta-grid">
        <div>
          <dt>Benutzername</dt>
          <dd>{user.username}</dd>
        </div>
        <div>
          <dt>Welt</dt>
          <dd>{user.world ? user.world.name : 'weltübergreifend'}</dd>
        </div>
        <div>
          <dt>Rolle</dt>
          <dd>{ROLE_LABELS[user.role]}</dd>
        </div>
      </dl>
    </section>

    <PlayerVerificationSection user={user} />

    <section className="card">
      <h2>Passwort ändern</h2>
      {generalError && <div className="flash flash-error">{generalError}</div>}

      <form className="form" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span>Aktuelles Passwort</span>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
          {fieldError('currentPassword') && (
            <small className="field-error">{fieldError('currentPassword')}</small>
          )}
        </label>

        <label className="field">
          <span>Neues Passwort</span>
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
          <small className="hint">Mindestens 10 Zeichen.</small>
          {fieldError('newPassword') && (
            <small className="field-error">{fieldError('newPassword')}</small>
          )}
        </label>

        <label className="field">
          <span>Neues Passwort wiederholen</span>
          <input
            type="password"
            autoComplete="new-password"
            value={newPasswordConfirm}
            onChange={(event) => setNewPasswordConfirm(event.target.value)}
            required
          />
          {fieldError('newPasswordConfirm') && (
            <small className="field-error">{fieldError('newPasswordConfirm')}</small>
          )}
        </label>

        <button type="submit" className="btn btn-primary" disabled={changePassword.isPending}>
          {changePassword.isPending ? 'Wird gespeichert …' : 'Passwort ändern'}
        </button>
      </form>
    </section>

    <TokenSection user={user} />
    </div>
  );
}
